import { api, ApiError } from "../api.js";
import { esc, displayName, toast } from "../utils.js";
import { postCardHtml, attachPostCardHandlers } from "../postCard.js";
import { planCardHtml, attachPlanCardHandlers } from "../divePlanCard.js";
import { navigate } from "../app.js";
import { getCurrentUser, isSiteAdmin } from "../auth.js";
import { linksSectionHtml, attachLinkHandlers } from "../links.js";
import {
  photoUploadHtml,
  attachPhotoUpload,
  infiniteGallery,
} from "../photoGallery.js";
import { tabSelectorHtml, attachTabSelector } from "../tabbedSection.js";
import { confirmAction } from "../confirmDialog.js";
import { followSwitchHtml, attachFollowSwitch } from "../followSwitch.js";

export async function render(container, params) {
  const communityId = params.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  /** Whether the reader may see inside: a private cohort's posts, events, photos,
   *  members and links are for its members (and site admins) only. */
  function canSeeInside(c) {
    return !c.is_private || c.is_member === true || isSiteAdmin();
  }

  /** The reader's pending invitation to this cohort, if any. */
  async function myInvitation() {
    if (!getCurrentUser()) return null;
    try {
      const mine = await api.get("/cohort-invitations");
      return mine.find((i) => i.community.id === Number(communityId)) || null;
    } catch {
      return null;
    }
  }

  async function load() {
    const [community, invitation] = await Promise.all([api.get(`/cohorts/${communityId}`), myInvitation()]);
    if (!canSeeInside(community)) {
      // Its name and description only: the rest would be refused.
      return { community, invitation, plans: [], posts: [], photoCount: 0, upcomingCount: 0 };
    }
    const [plans, posts, photoPage, upcomingPage] = await Promise.all([
      api.get("/dive-plans", { community_id: communityId }),
      api.get("/posts", { community_id: communityId }),
      api.getPage(`/cohorts/${communityId}/photos`, { limit: 1 }),
      // Counted by the server rather than taken from `plans` above: the
      // label should say how many dives there are to join, and the past
      // ones are behind a sub-tab nobody lands on.
      api.getPage("/dive-plans", {
        query: { community_id: communityId, when: "upcoming" },
        limit: 1,
      }),
    ]);
    return {
      community,
      invitation,
      plans,
      posts,
      photoCount: photoPage.total ?? 0,
      upcomingCount: upcomingPage.total ?? 0,
    };
  }

  let community, invitation, plans, posts, photoCount, upcomingCount;
  try {
    ({ community, invitation, plans, posts, photoCount, upcomingCount } = await load());
  } catch (err) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Cohort not found.</div></div>`;
    return;
  }

  // Upcoming/Past sub-tabs for the Dive Events section, matching the
  // top-level Dive Events page (divePlans.js). All of a community's
  // plans are already fetched above in one call, so this just filters
  // and re-sorts them client-side per tab rather than making two
  // separate ?when= requests.
  let planTab = "upcoming";
  let activeTab = "posts";
  // The gallery pages in as you scroll; stopped when the tab changes.
  let activeGallery = null;

  function renderPlansSection() {
    const section = container.querySelector("#community-plans-section");
    if (!section) return;

    const upcoming = plans
      .filter((p) => !p.is_past)
      .sort((a, b) => new Date(a.planned_date) - new Date(b.planned_date));
    const past = plans
      .filter((p) => p.is_past)
      .sort((a, b) => new Date(b.planned_date) - new Date(a.planned_date));
    const visible = planTab === "upcoming" ? upcoming : past;

    section.querySelectorAll("#plan-tabs .nav-link").forEach((t) => {
      t.classList.toggle("active", t.dataset.tab === planTab);
    });

    const content = section.querySelector("#community-plans");
    content.innerHTML = visible.length
      ? visible.map(planCardHtml).join("")
      : `<p class="text-slate">${planTab === "upcoming" ? "No upcoming dive events for this cohort yet." : "No past dive events for this cohort yet."}</p>`;
    attachPlanCardHandlers(content);
  }

  /** Paints the active tab, its action button and its panel.
   *
   * Each panel is rebuilt on switch rather than hidden, so handlers are
   * bound to the elements actually on screen and a stale panel cannot
   * accumulate duplicate listeners. */
  function renderTabs() {
    const host = container.querySelector("#cohort-tabs-host");
    const panel = container.querySelector("#cohort-tab-panel");
    const action = container.querySelector("#cohort-tab-action");
    if (!host || !panel) return;

    host.innerHTML = tabSelectorHtml(
      [
        { id: "posts", label: "Posts", count: posts.length },
        { id: "events", label: "Dive Events", count: upcomingCount },
        { id: "photos", label: "Photos", count: photoCount },
      ],
      activeTab,
      { id: "cohort-tabs" }
    );
    attachTabSelector(host, (id) => {
      activeTab = id;
      renderTabs();
    });

    if (activeTab === "posts") {
      action.innerHTML = `<button class="btn btn-sm btn-outline-primary" id="post-here-btn"><i class="bi bi-plus-lg me-1"></i>Post</button>`;
      panel.innerHTML = posts.length
        ? posts.map(postCardHtml).join("")
        : `<p class="text-slate mb-0">No posts in this cohort yet.</p>`;
      attachPostCardHandlers(panel);
      action.querySelector("#post-here-btn").addEventListener("click", () => {
        const q = new URLSearchParams();
        q.set("community_id", community.id);
        navigate(`/create-post?${q.toString()}`);
      });
    } else if (activeTab === "events") {
      action.innerHTML = `<button class="btn btn-sm btn-outline-primary" id="plan-here-btn"><i class="bi bi-plus-lg me-1"></i>Plan</button>`;
      panel.innerHTML = `
        <div id="community-plans-section">
          <ul class="nav dc-tab-bar mb-3" id="plan-tabs">
            <li class="nav-item"><button class="nav-link" data-tab="upcoming">Upcoming</button></li>
            <li class="nav-item"><button class="nav-link" data-tab="past">Past</button></li>
          </ul>
          <div id="community-plans"></div>
        </div>`;
      renderPlansSection();
      panel.querySelectorAll("#plan-tabs .nav-link").forEach((t) => {
        t.addEventListener("click", () => {
          planTab = t.dataset.tab;
          renderPlansSection();
        });
      });
      action.querySelector("#plan-here-btn").addEventListener("click", () => {
        const q = new URLSearchParams();
        q.set("community_id", community.id);
        navigate(`/create-dive-plan?${q.toString()}`);
      });
    } else {
      // Uploading needs an account, and now membership too — the API
      // refuses photos into a cohort you are not in. A button that only
      // ever fails is worse than no button.
      action.innerHTML =
        getCurrentUser() && community.is_member === true
          ? photoUploadHtml("cohort-photo-add")
          : "";
      attachPhotoUpload(action, {
        id: "cohort-photo-add",
        endpoint: `/cohorts/${communityId}/photos`,
        onUploaded: async () => {
          photoCount = (await api.getPage(`/cohorts/${communityId}/photos`, { limit: 1 })).total ?? 0;
          renderTabs();
        },
      });
      const me = getCurrentUser();
      activeGallery?.destroy();
      activeGallery = infiniteGallery(panel, {
        path: `/cohorts/${communityId}/photos`,
        emptyMessage:
          "No photos in this cohort yet. Photos attached to posts here show up in this gallery.",
        // Whoever uploaded it, or a moderator — the same rule the API
        // enforces.
        canDelete: (photo) => me != null && (photo.owner_id === me.id || isSiteAdmin()),
        onDeleted: async () => renderTabs(),
      });
    }
  }

  function renderLinks() {
    const host = container.querySelector("#links-host");
    if (!host) return;
    // A cohort has owners, so only its admins curate the links — unlike
    // a dive site, where anyone may add one. Mirrors the API.
    const isAdmin = community.my_role === "admin";
    host.innerHTML = linksSectionHtml(community.links, {
      canAdd: isAdmin,
      canManage: () => isAdmin,
    });
    attachLinkHandlers(host, {
      basePath: `/cohorts/${communityId}/links`,
      reload: async () => {
        community = await api.get(`/cohorts/${communityId}`);
        renderLinks();
      },
    });
  }

  /** Join / Member (leave) / Invitation only, or an invitation to answer. */
  function membershipHtml(isMember) {
    if (invitation && !isMember) {
      const who = invitation.invited_by ? esc(displayName(invitation.invited_by)) : "A cohort admin";
      return `
        <div class="alert alert-info d-flex flex-wrap align-items-center gap-2 mb-0" id="invitation-banner">
          <span class="me-auto"><i class="bi bi-envelope-open me-1"></i>${who} invited you to join this cohort.</span>
          <button class="btn btn-sm btn-primary" id="invitation-accept">Accept</button>
          <button class="btn btn-sm btn-outline-secondary" id="invitation-decline">Decline</button>
        </div>`;
    }
    if (!isMember && community.is_private) {
      return `<div class="text-slate small" id="invitation-only"><i class="bi bi-lock me-1"></i>Members join by invitation from the cohort's admins.</div>`;
    }
    return `
      <button class="btn ${isMember ? "btn-outline-primary" : "btn-primary"}" id="membership-btn">
        <i class="bi ${isMember ? "bi-check-lg" : "bi-people"} me-1"></i>${isMember ? "Member" : "Join cohort"}
      </button>`;
  }

  async function answerInvitation(action, btn) {
    btn.disabled = true;
    try {
      await api.post(`/cohort-invitations/${invitation.id}/${action}`);
      ({ community, invitation, plans, posts, photoCount, upcomingCount } = await load());
      if (action === "accept") toast(`Welcome to ${community.name}.`, "success");
      renderAll();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
      btn.disabled = false;
    }
  }

  function renderAll() {
    const isMember = community.is_member === true;
    const isAdmin = community.my_role === "admin";
    const inside = canSeeInside(community);
    const memberCount = `<i class="bi bi-people me-1"></i>${community.member_count ?? 0} members`;

    container.innerHTML = `
      <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex align-items-start justify-content-between mb-2">
            <div class="d-flex align-items-start">
              <div class="dc-avatar dc-avatar-lg me-3" style="background-color:var(--dc-teal-100);">
                <i class="bi bi-people" style="color:var(--dc-teal-700);font-size:2rem;"></i>
              </div>
              <div>
                <h1 class="display-heading h4 mb-1">${esc(community.name)}
                  ${community.is_private ? `<span class="badge text-bg-light border align-middle ms-1" id="private-badge" style="font-size:0.75rem;"><i class="bi bi-lock me-1"></i>Private</span>` : ""}
                </h1>
                ${inside
                  ? `<a href="#/cohort/${community.id}/members" class="text-slate small text-decoration-none">
                      ${memberCount}<i class="bi bi-chevron-right ms-1" style="font-size:0.7rem;"></i></a>`
                  : `<span class="text-slate small">${memberCount}</span>`}
                ${community.created_by ? `<div class="text-slate small">Started by ${esc(displayName(community.created_by))}</div>` : ""}
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              ${isMember && community.chat ? `<a class="btn btn-sm btn-primary" href="#/cohort/${community.id}/chat" id="chat-btn">
                  <i class="bi bi-chat-dots me-1"></i>Chat${community.chat.unread_count
                    ? ` <span class="badge rounded-pill text-bg-danger ms-1" id="chat-unread"
                          aria-label="${community.chat.unread_count} unread">${community.chat.unread_count}</span>`
                    : ""}</a>` : ""}
              ${isAdmin ? `<span class="badge text-bg-light border">Admin</span>` : ""}
              ${isAdmin ? `<button class="btn btn-sm btn-outline-primary" id="invite-btn" title="Invite people"><i class="bi bi-person-plus me-1"></i>Invite</button>` : ""}
              ${isAdmin ? `<button class="btn btn-sm btn-outline-secondary" id="edit-btn" title="Edit cohort"><i class="bi bi-pencil"></i></button>` : ""}
            </div>
          </div>
          ${community.description ? `<p class="mt-3 mb-3 dc-user-text">${esc(community.description)}</p>` : `<div class="mb-3"></div>`}
          ${membershipHtml(isMember)}
          ${isMember ? followSwitchHtml({
            following: community.is_following,
            label: "Follow this cohort",
            help: "Its posts appear in your Following feed. Turn this off to mute the cohort.",
          }) : ""}
        </div>
      </div>
      ${inside
        ? `<div id="links-host"></div>
           <div class="card mb-4">
             <div class="card-body">
               <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                 <div id="cohort-tabs-host"></div>
                 <div id="cohort-tab-action"></div>
               </div>
               <div id="cohort-tab-panel"></div>
             </div>
           </div>`
        : `<div class="dc-empty" id="private-notice"><i class="bi bi-lock"></i>
             <div class="fw-semibold">This cohort is private</div>
             <div>Its posts, dive events, photos and members are visible to its members only.</div>
           </div>`}`;

    container.querySelector("#back-btn").addEventListener("click", () => history.back());
    attachFollowSwitch(container, {
      path: `/cohorts/${communityId}`,
      onChange: (updated) => (community.is_following = updated.is_following),
    });
    if (inside) {
      renderLinks();
      renderTabs();
    }
    container.querySelector("#invite-btn")?.addEventListener("click", () => navigate(`/cohort/${community.id}/members`));
    container.querySelector("#invitation-accept")?.addEventListener("click", (e) => answerInvitation("accept", e.currentTarget));
    container.querySelector("#invitation-decline")?.addEventListener("click", (e) => answerInvitation("decline", e.currentTarget));

    const editBtn = container.querySelector("#edit-btn");
    if (editBtn) {
      editBtn.addEventListener("click", () => navigate(`/cohort/${community.id}/edit`));
    }

    container.querySelector("#membership-btn")?.addEventListener("click", async (e) => {
      const btn = e.currentTarget;
      if (isMember && community.is_private && !(await confirmAction({
        title: "Leave this private cohort?",
        body: "You'll need a new invitation from its admins to rejoin, and its posts, dive events and photos will be hidden from you.",
        confirmLabel: "Leave",
      }))) return;
      btn.disabled = true;
      try {
        const path = `/cohorts/${communityId}/${isMember ? "leave" : "join"}`;
        await api.post(path);
        ({ community, invitation, plans, posts, photoCount, upcomingCount } = await load());
        renderAll();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
        btn.disabled = false;
      }
    });
  }

  renderAll();
}
